import { useState } from 'react';
import { Icon } from '~/components/ui';
import { useT } from '~/i18n';
import CVScoreCard from './CVScoreCard';
import { parseCVSections } from '../lib/screening';
import type { ScreeningCVResult } from '../api/screeningClient';

/**
 * La hoja de vida recién escrita, para leerla antes de usarla.
 *
 * NO es un textarea con el texto adentro. Lo que devuelve el modelo es un
 * documento —nombre, resumen, empleos, habilidades— y mostrarlo como un bloque
 * editable de sesenta líneas invita a lo único que no queremos: que alguien
 * "arregle" a mano su CV agregando un dato que no declaró. Acá se lee como lo
 * va a leer el empleador, y lo único que se puede pedir es que suene distinto.
 *
 * Ese es también el motivo de la copia del campo de ajuste: el feedback cambia
 * la REDACCIÓN, no los hechos. Si alguien pide "pon que manejé montacargas" y
 * eso no está en sus respuestas, el modelo tiene instrucción de ignorarlo. Para
 * agregar datos el camino es el cuestionario, y por eso hay un enlace de vuelta.
 */

interface Props {
  result: ScreeningCVResult;
  onConfirm: () => void;
  confirming: boolean;
  onRegenerate: (feedback: string) => void;
  regenerating: boolean;
  error: string | null;
  /** Volver al cuestionario con las respuestas todavía cargadas. */
  onBackToForm: () => void;
  /**
   * "Usar esta hoja de vida": sólo cuando `is_primary === false`, o sea cuando
   * la persona ya postulaba con otra y ésta quedó guardada al lado.
   */
  onUseThis?: () => void;
  promoting?: boolean;
  /**
   * «Agregar estos datos»: con la hoja de vida por piezas lleva a Mejorar mi
   * hoja de vida (el envío ya creó el perfil) y no al cuestionario entero.
   * Ausente, vuelve al cuestionario como antes.
   */
  onAddMissing?: () => void;
  /** Ya hay perfil por piezas: «Volver a mis respuestas» no se ofrece (D10). */
  hasProfile?: boolean;
}

export default function ScreeningReview({
  result,
  onConfirm,
  confirming,
  onRegenerate,
  regenerating,
  error,
  onBackToForm,
  onUseThis,
  promoting = false,
  onAddMissing,
  hasProfile = false,
}: Props) {
  const t = useT('screening');
  const [asking, setAsking] = useState(false);
  const [feedback, setFeedback] = useState('');

  const sections = parseCVSections(result.cv_text);
  const left = result.regenerations_left;
  const busy = confirming || regenerating || promoting;
  // Ya había otra hoja de vida de postular y ésta no vino a reemplazarla: acá
  // "continuar" a secas sería dejar la nueva guardada sin que nadie la use, así
  // que se pregunta con cuál seguir.
  const choosing = result.is_primary === false && Boolean(onUseThis);
  const addMissing = onAddMissing ?? onBackToForm;

  return (
    <div className="mx-auto w-full max-w-2xl py-6 space-y-5">
      <header className="space-y-1">
        <span className="brand-label">{t.review.eyebrow}</span>
        <h1 className="text-2xl font-semibold">{t.review.title}</h1>
        <p className="hush text-sm">{t.review.lede}</p>
      </header>

      {result.summary && (
        <div className="card">
          <div className="card-body">
            <span className="brand-label">{t.review.summary}</span>
            <p className="text-sm mt-2" style={{ lineHeight: 1.6 }}>
              {result.summary}
            </p>
          </div>
        </div>
      )}

      <div className="card rise">
        <div className="card-body space-y-5">
          {sections.map((section, i) => (
            <section key={i} className="space-y-2">
              {section.heading && <h2 className="brand-label">{section.heading}</h2>}
              {section.lines.map((line, j) =>
                line.bullet ? (
                  <div key={j} className="flex items-start gap-2">
                    <span
                      aria-hidden="true"
                      className="shrink-0"
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: 999,
                        background: 'var(--purple-cta)',
                        marginTop: 8,
                      }}
                    />
                    <p className="text-sm" style={{ lineHeight: 1.55 }}>
                      {line.text}
                    </p>
                  </div>
                ) : (
                  <p
                    key={j}
                    className={
                      i === 0 && j === 0 ? 'text-lg font-semibold' : 'text-sm'
                    }
                    style={{ lineHeight: 1.55 }}
                  >
                    {line.text}
                  </p>
                ),
              )}
            </section>
          ))}
        </div>
      </div>

      {/* ── La nota, pegada al documento que califica ─────────────────────────
          Acá y no en una pantalla posterior porque acá es donde se decide: al
          lado de "Se ve bien, continuar" está el número que dice si de verdad
          se ve bien, y la lista de lo que falta con el camino de vuelta al
          cuestionario. Sin esto, "continuar" es la única opción visible y todo
          el mundo la toma.

          `score` puede venir en null si el modelo no alcanzó a calificar: el CV
          se guardó igual, y en ese caso no se dibuja nada. Un cero inventado
          sería peor que no mostrar nota. */}
      {result.score && (
        <CVScoreCard
          detail={result.score}
          scope={t.review.scoreScope}
          onAddGap={addMissing}
          action={
            result.score.missing.length > 0 ? (
              <>
                <button type="button" className="btn btn-primary btn-sm" onClick={addMissing}>
                  {t.review.addMissing}
                </button>
                {/* «Vuelves al cuestionario...» solo es cierto por el camino viejo. */}
                {!onAddMissing && <span className="text-xs hush">{t.review.addMissingHint}</span>}
              </>
            ) : undefined
          }
        />
      )}

      {result.skills.length > 0 && (
        <div className="card">
          <div className="card-body">
            <span className="brand-label">{t.review.skills}</span>
            <div className="flex flex-wrap gap-2 mt-3">
              {result.skills.map((skill) => (
                <span key={skill} className="chip">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {result.is_primary === false && (
        <div className="alert alert-warning text-sm">
          {t.review.notPrimary}
        </div>
      )}

      {error && <div className="alert alert-error text-sm">{error}</div>}

      {!asking && (
        <div className="flex flex-col gap-3">
          {choosing ? (
            <>
              <button className="btn btn-primary w-full" onClick={onUseThis} disabled={busy}>
                {promoting && <span className="loading loading-spinner loading-sm" />}
                {t.review.useThis}
              </button>
              <button className="btn btn-outline w-full" onClick={onConfirm} disabled={busy}>
                {confirming && <span className="loading loading-spinner loading-sm" />}
                {t.review.keepCurrent}
              </button>
            </>
          ) : (
            <button className="btn btn-primary w-full" onClick={onConfirm} disabled={busy}>
              {confirming && <span className="loading loading-spinner loading-sm" />}
              {t.review.confirm}
            </button>
          )}

          <button
            className="btn btn-outline w-full"
            onClick={() => setAsking(true)}
            disabled={busy || left <= 0}
          >
            {t.review.improve}
          </button>

          {left > 0 ? (
            <p className="text-xs hush text-center">{t.review.adjustmentsLeft(left)}</p>
          ) : (
            <p className="text-xs hush text-center">
              {t.review.limitBefore}{' '}
              <button type="button" className="link link-primary" onClick={onBackToForm}>
                {t.review.limitLink}
              </button>{' '}
              {t.review.limitAfter}
            </p>
          )}
        </div>
      )}

      {asking && (
        <div className="card rise">
          <div className="card-body space-y-3">
            <label className="label pb-0" htmlFor="cv_feedback">
              <span className="label-text font-medium">{t.review.feedbackLabel}</span>
            </label>
            <textarea
              id="cv_feedback"
              className="textarea w-full"
              rows={4}
              maxLength={1000}
              autoFocus
              value={feedback}
              placeholder={t.review.feedbackPlaceholder}
              onChange={(e) => setFeedback(e.target.value)}
            />
            <p className="text-xs hush">
              {t.review.rewriteBefore}{' '}
              <button type="button" className="link link-primary" onClick={onBackToForm}>
                {t.review.rewriteLink}
              </button>
              {t.review.rewriteAfter}
            </p>

            <div className="flex gap-3">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setAsking(false);
                  setFeedback('');
                }}
                disabled={regenerating}
              >
                {t.review.cancel}
              </button>
              <button
                className="btn btn-primary flex-1 gap-2"
                disabled={regenerating || !feedback.trim()}
                onClick={() => {
                  onRegenerate(feedback.trim());
                  setAsking(false);
                  setFeedback('');
                }}
              >
                {regenerating ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  <Icon name="refresh" size={16} />
                )}
                {t.review.redo}
              </button>
            </div>
          </div>
        </div>
      )}

      {!hasProfile && (
        <p className="text-xs hush text-center">
          <button type="button" className="link" onClick={onBackToForm} disabled={busy}>
            {t.review.backToAnswers}
          </button>
        </p>
      )}
    </div>
  );
}
